// The invite Laila drafts (lailaChat's draft_worker_invite tool): what the
// card holds, what is left to pick before it can go, and the body the app
// sends. The mobile app has the same file (screens/AIChat/components/cards).
import uniq from 'lodash/uniq';

import { CreateWorkerInvitesPayload } from '../../../../api/types';

export type InvitePayScheme = 'cis' | 'self-employed';

export interface InviteChoice {
  id: string;
  name: string;
  detail?: string;
  // A site's shifts.
  shifts?: InviteChoice[];
}

export interface InviteTarget {
  // What the user called it.
  asked?: string;
  chosen?: InviteChoice;
  options: InviteChoice[];
  // The user may not add people here; it stays out of the invite.
  unavailable?: boolean;
}

export type InviteDraftState = 'sending' | 'started' | 'failed' | 'cancelled';

// Someone new, invited by text: E.164 when valid, else as typed.
export interface NewPerson {
  phone: string;
  valid: boolean;
}

// Someone already in the company: added straight away, no text.
export interface VycePerson {
  userId: string;
  name: string;
  detail?: string;
}

export type InvitePerson = NewPerson | VycePerson;

export const isOnVyce = (person: InvitePerson): person is VycePerson =>
  'userId' in person && !!person.userId;

export const personKey = (person: InvitePerson) => (isOnVyce(person) ? person.userId : person.phone);

// The guided invite: what Laila asks next. 'which' is a name that matched several people.
export type InviteStep =
  | 'group'
  | 'who'
  | 'which'
  | 'module'
  | 'team'
  | 'site'
  | 'shift'
  | 'pay_schedule'
  | 'scheme'
  | 'ready';

export type InviteModule = 'team' | 'site' | 'paySchedule';

// People taken from a team, site or pay schedule: all of them (but `except`),
// or the ones the user ticks in the card.
export interface InviteGroup extends InviteTarget {
  kind: InviteModule;
  everyone: boolean;
  count?: number;
  // Up to 100, to tick.
  members?: InviteChoice[];
  except?: VycePerson[];
  // The user may not see who is in it.
  membersUnavailable?: boolean;
}

export interface InviteDraftData {
  companyId: string;
  people: InvitePerson[];
  group?: InviteGroup | null;
  // The user's ticks, kept by the app: a new version of the card carries them over.
  local?: { picked?: string[]; excluded?: string[] };
  step?: InviteStep;
  // Names said that matched several people (options) or nobody (none).
  unresolved?: { asked: string; options: InviteChoice[] }[];
  // Where they could go, while nothing is picked.
  offer?: InviteModule[];
  team: InviteTarget | null;
  location: (InviteTarget & { shift: InviteTarget | null }) | null;
  paySchedule: (InviteTarget & { scheme: InvitePayScheme | null }) | null;
  // Set by the app once the user acts on the card; none while it's a draft.
  state?: InviteDraftState;
  // The Vyce API's words on how it went.
  message?: string;
}

export type InviteDraftChange =
  // A person's phone, or user id when they're on Vyce.
  | { type: 'removePerson'; key: string }
  | { type: 'pick'; target: 'team' | 'location' | 'shift' | 'paySchedule'; id: string }
  | { type: 'scheme'; scheme: InvitePayScheme }
  // Leaves a team, site or pay schedule out of the invite.
  | { type: 'drop'; target: 'team' | 'location' | 'paySchedule' }
  // Ticks someone from the group: picks them, or for a whole group leaves them out.
  | { type: 'tick'; id: string }
  | { type: 'tickAll'; on: boolean };

export const PAY_SCHEMES: { value: InvitePayScheme; label: string }[] = [
  { value: 'cis', label: 'CIS self-employed' },
  { value: 'self-employed', label: 'Self-employed' },
];

const pick = <T extends InviteTarget>(target: T, id: string): T => {
  const chosen = target.options.find(option => option.id === id);
  return chosen ? { ...target, chosen } : target;
};

export const applyInviteChange = (data: InviteDraftData, change: InviteDraftChange): InviteDraftData => {
  switch (change.type) {
    case 'removePerson':
      return { ...data, people: data.people.filter(person => personKey(person) !== change.key) };
    case 'scheme':
      return data.paySchedule
        ? { ...data, paySchedule: { ...data.paySchedule, scheme: change.scheme } }
        : data;
    case 'drop':
      return { ...data, [change.target]: null };
    case 'tick': {
      const field = data.group?.everyone ? 'excluded' : 'picked';
      const ids = data.local?.[field] ?? [];
      return {
        ...data,
        local: {
          ...data.local,
          [field]: ids.includes(change.id) ? ids.filter(id => id !== change.id) : [...ids, change.id],
        },
      };
    }
    case 'tickAll': {
      const ids = (data.group?.members ?? []).map(member => member.id);
      return data.group?.everyone
        ? { ...data, local: { ...data.local, excluded: change.on ? [] : ids } }
        : { ...data, local: { ...data.local, picked: change.on ? ids : [] } };
    }
    case 'pick': {
      if (change.target === 'team' && data.team) {
        return { ...data, team: pick(data.team, change.id) };
      }
      if (change.target === 'paySchedule' && data.paySchedule) {
        return { ...data, paySchedule: pick(data.paySchedule, change.id) };
      }
      if (change.target === 'shift' && data.location?.shift) {
        return { ...data, location: { ...data.location, shift: pick(data.location.shift, change.id) } };
      }
      if (change.target === 'location' && data.location) {
        const location = pick(data.location, change.id);
        if (location.chosen?.id === data.location.chosen?.id) {
          return data;
        }
        // Another site, another set of shifts; one shift needs no question.
        const shifts = location.chosen?.shifts ?? [];
        return {
          ...data,
          location: {
            ...location,
            shift: { options: shifts, chosen: shifts.length === 1 ? shifts[0] : undefined },
          },
        };
      }
      return data;
    }
  }
};

const included = (target: InviteTarget | null): target is InviteTarget => !!target && !target.unavailable;

// What stops the invite going, in the words the card shows.
export const inviteProblems = (data: InviteDraftData): string[] => {
  const problems: string[] = [];
  data.people
    .filter((person): person is NewPerson => !isOnVyce(person) && !person.valid)
    .forEach(person => problems.push(`${person.phone} isn’t a mobile number`));
  const group = data.group?.chosen ? data.group : null;
  if (group && !group.everyone && !data.local?.picked?.length) {
    problems.push(`Tick who should go from ${group.chosen!.name}`);
  } else if (!inviteCount(data)) {
    problems.push(
      group ? `Everyone in ${group.chosen!.name} is left out` : 'Add at least one mobile number'
    );
  }
  if (included(data.team) && !data.team.chosen) {
    problems.push('Pick a team');
  }
  if (data.location && included(data.location)) {
    if (!data.location.chosen) {
      problems.push('Pick a site');
    } else if (!data.location.shift?.chosen) {
      problems.push(`Pick a shift for ${data.location.chosen.name}`);
    }
  }
  if (data.paySchedule && included(data.paySchedule)) {
    if (!data.paySchedule.chosen) {
      problems.push('Pick a pay schedule');
    } else if (!data.paySchedule.scheme) {
      problems.push('Choose how to engage them');
    }
  }
  if (!included(data.team) && !included(data.location) && !included(data.paySchedule)) {
    problems.push('Ask Laila to add a team, site or pay schedule');
  }
  return problems;
};

export const invitePayload = (data: InviteDraftData): CreateWorkerInvitesPayload => {
  const items: CreateWorkerInvitesPayload['items'] = [];
  if (included(data.team) && data.team.chosen) {
    items.push({ resource_type: 'team', resource_id: data.team.chosen.id });
  }
  const { location, paySchedule } = data;
  if (location && included(location) && location.chosen && location.shift?.chosen) {
    items.push({
      resource_type: 'location',
      resource_id: location.chosen.id,
      shift_id: location.shift.chosen.id,
    });
  }
  if (paySchedule && included(paySchedule) && paySchedule.chosen && paySchedule.scheme) {
    items.push({
      resource_type: 'pay_schedule',
      resource_id: paySchedule.chosen.id,
      pay_scheme: paySchedule.scheme,
    });
  }
  return {
    items,
    contacts: data.people
      .filter((person): person is NewPerson => !isOnVyce(person) && person.valid)
      .map(person => ({ phone: person.phone })),
    // Already in the company: added straight away, no text.
    user_ids: [
      ...data.people.filter(isOnVyce).map(person => person.userId),
      ...(data.group?.chosen && !data.group.everyone ? data.local?.picked ?? [] : []),
    ],
    // A whole group goes as the group itself, so the backend takes everyone in it.
    source: {
      team_ids: wholeGroup(data, 'team'),
      location_ids: wholeGroup(data, 'site'),
      pay_schedule_ids: wholeGroup(data, 'paySchedule'),
      all_users: false,
    },
    exclude_user_ids: data.group?.chosen && data.group.everyone ? groupLeftOut(data) : [],
    send_report: true,
  };
};

// "Bricklayers · Riverside Wharf, Day · Weekly CIS".
export const inviteWhere = (data: InviteDraftData) =>
  [
    included(data.team) && data.team.chosen?.name,
    data.location &&
      included(data.location) &&
      data.location.chosen &&
      [data.location.chosen.name, data.location.shift?.chosen?.name].filter(Boolean).join(', '),
    data.paySchedule && included(data.paySchedule) && data.paySchedule.chosen?.name,
  ]
    .filter(Boolean)
    .join(' · ');

const wholeGroup = (data: InviteDraftData, kind: InviteModule) =>
  data.group?.chosen && data.group.everyone && data.group.kind === kind ? [data.group.chosen.id] : [];

// Who a whole group leaves out: those Laila was told to, and those the user unticked.
export const groupLeftOut = (data: InviteDraftData) =>
  uniq([...(data.group?.except ?? []).map(person => person.userId), ...(data.local?.excluded ?? [])]);

// How many of the group go: everyone but those left out, or the ones ticked.
export const groupCount = (data: InviteDraftData) => {
  const group = data.group;
  if (!group?.chosen) {
    return 0;
  }
  return group.everyone
    ? Math.max((group.count ?? 0) - groupLeftOut(data).length, 0)
    : data.local?.picked?.length ?? 0;
};

export const inviteCount = (data: InviteDraftData) =>
  data.people.filter(person => isOnVyce(person) || person.valid).length + groupCount(data);

// The guided invite's three steps: who, what kind of place, which one.
export const STEP_NUMBER: Record<Exclude<InviteStep, 'ready'>, number> = {
  group: 1,
  who: 1,
  which: 1,
  module: 2,
  team: 3,
  site: 3,
  shift: 3,
  pay_schedule: 3,
  scheme: 3,
};

// Still being filled in: the card asks, rather than shows a draft to send.
export const isGuided = (data: InviteDraftData) => !!data.step && data.step !== 'ready' && !data.state;

export const MODULE_ANSWERS: Record<InviteModule, string> = {
  team: 'A team',
  site: 'A site',
  paySchedule: 'A pay schedule',
};

// What a tap on one of several people says to Laila: the name, and the end of
// the number when two share it.
export const personAnswer = (choice: InviteChoice, options: InviteChoice[]) => {
  const twins = options.filter(option => option.name === choice.name).length > 1;
  const ends = choice.detail?.match(/ends (\d+)/)?.[1];
  return twins && ends ? `${choice.name}, ends ${ends}` : choice.name;
};

export const people = (count: number) => (count === 1 ? '1 person' : `${count} people`);

// The Vyce API's reason, when it gave one in words.
export const inviteErrorMessage = (error: any): string => {
  const status = error?.response?.status;
  const detail = error?.response?.data?.detail;
  if (status === 503) {
    return 'The invite service didn’t answer, so nobody got anything. Try again in a minute.';
  }
  if (typeof detail === 'string' && detail) {
    return detail;
  }
  if (Array.isArray(detail) && typeof detail[0]?.msg === 'string') {
    return detail[0].msg;
  }
  if (!error?.response) {
    return 'No connection, so nothing was sent. Try again when you’re back online.';
  }
  return 'The invites couldn’t be sent. Nothing went out.';
};

// "+447700900123" as people write it: "07700 900123".
export const displayPhone = (phone: string) =>
  /^\+447\d{9}$/.test(phone) ? `0${phone.slice(3, 7)} ${phone.slice(7)}` : phone;
